'use client';

import { type Context, createContext, use } from 'react';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type PortfolioViewerMode = 'own' | 'other';
export interface PortfolioContextValue {
  /**
   * Discriminated viewer-identity flag — `'own'` when the authenticated user
   * is viewing their own portfolio, `'other'` when viewing someone else's.
   */
  viewerMode: PortfolioViewerMode;
  /** Resolved user id used for SDK calls (own user id, or target id). */
  userId: string;
  /** Wallet or user identifier used by wallet-scoped financial endpoints. */
  financialQueryId: string;
  /** The viewed profile's primary wallet, independent of the signed-in viewer. */
  primaryWalletPubkey: string | null;
  /**
   * Wallet pubkey used for per-wallet holdings/trading queries. Single
   * value — the SELECTED wallet in single-wallet mode, the primary wallet
   * in "all wallets" mode. NOT the realtime subscription identity (use
   * `walletPubkeys` for that — a single subscription misses the other
   * wallets' chain events).
   */
  walletPubkey: string;
  /**
   * Every wallet the viewed user owns. Threaded into per-user-feed hooks
   * as `walletPubkeys:` (array) so realtime subscriptions cover EVERY
   * wallet — a single-wallet subscription drifts as soon as the user
   * trades from a non-primary wallet. Own viewer reads from `useWallets()`;
   * other viewer reads from `useGetUser({…}).data.wallets[]`.
   */
  walletPubkeys: readonly string[];
  /** Whether all-wallet aggregation and selection are available. */
  multiWalletEnabled: boolean;
}
export const PortfolioContext: Context<PortfolioContextValue | null> = createContext<PortfolioContextValue | null>(null);

/**
 * Read the portfolio context. Throws when called outside a provider so
 * consumer-error is loud instead of producing an undefined `viewerMode`.
 */
export function usePortfolioContext(): PortfolioContextValue {
  const ctx = use(PortfolioContext);
  if (ctx === null) {
    throw new Error('usePortfolioContext must be used within an OwnPortfolio or OtherPortfolio');
  }
  return ctx;
}

/** True when the viewer is the authenticated user inspecting their own profile. */
export function isOwnViewer(value: PortfolioContextValue): boolean {
  return value.viewerMode === 'own';
}